import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebase/firebaseConfig';
import { ref, onValue } from 'firebase/database';
import { changelogUpdates } from '../data/changelogData';
import './Changelog.css';

function Changelog() {
  const [allUpdates, setAllUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const { pageNumber } = useParams();
  const navigate = useNavigate();
  
  const ITEMS_PER_PAGE = 5;
  const currentPage = parseInt(pageNumber) || 1;

  useEffect(() => {
    const logsRef = ref(db, 'changelog');
    const unsubscribe = onValue(logsRef, (snapshot) => {
      let parsed = [];
      if (snapshot.exists()) {
        const data = snapshot.val();
        parsed = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
      } else {
        parsed = [...changelogUpdates];
      }
      
      // Sort by version descending (v1.3.3 > v1.3.2)
      parsed.sort((a, b) => b.version.localeCompare(a.version, undefined, { numeric: true, sensitivity: 'base' }));
      setAllUpdates(parsed);
      
      // Expand the first item if we are on page 1
      if (currentPage === 1 && parsed.length > 0) {
        setExpandedId(parsed[0].id || 0);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentPage]);

  const toggleAccordion = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getBadgeClass = (type) => {
    switch(type) {
      case 'feature': return 'badge-feature';
      case 'bugfix': 
      case 'fix': return 'badge-bugfix';
      case 'launch': return 'badge-launch';
      case 'security': return 'badge-security';
      default: return 'badge-default';
    }
  };

  if (loading) {
    return (
      <div className="changelog-page animate-up">
        <div className="changelog-header-wrapper">
          <div className="container changelog-container">
            <h1 className="changelog-hero-title">Changelog</h1>
          </div>
        </div>
        <div className="container" style={{textAlign: 'center', padding: '100px'}}>
           <div className="loader" style={{margin: '0 auto'}}></div>
           <p style={{color: '#8b949e', marginTop: '20px'}}>Syncing historical updates...</p>
        </div>
      </div>
    );
  }

  // Calculate pagination
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedUpdates = allUpdates.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  const totalPages = Math.ceil(allUpdates.length / ITEMS_PER_PAGE);

  const handlePageChange = (newPage) => {
    if (newPage === 1) {
      navigate('/changelog');
    } else {
      navigate(`/changelog/page/${newPage}`);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="changelog-page animate-up">
      <div className="changelog-header-wrapper">
        <div className="container changelog-container">
          <h1 className="changelog-hero-title">
            <span className="changelog-indicator-bar"></span>
            Changelog
          </h1>
          <p className="changelog-subtitle">New updates and improvements to DevTracker.</p>
        </div>
      </div>

      <div className="container changelog-container">
        <div className="changelog-timeline-section">
          {paginatedUpdates.length > 0 ? (
            paginatedUpdates.map((update, index) => {
              const itemId = update.id || `static-${startIndex + index}`;
              const isExpanded = expandedId === itemId;
              
              return (
                <div key={itemId} className={`timeline-row ${isExpanded ? 'active' : ''}`}>
                  {/* Left Column: Version and Date */}
                  <div className="timeline-meta">
                    <div className="meta-version">{update.version}</div>
                    <div className="meta-date">{update.date}</div>
                  </div>

                  {/* Vertical Divider logic: Spine & Dot */}
                  <div className="timeline-spine">
                    <div className="timeline-dot"></div>
                  </div>

                  {/* Right Column: Update Content */}
                  <div className="timeline-content">
                    <div className="update-card" onClick={() => toggleAccordion(itemId)}>
                      <div className="update-header-info" data-version-date={`${update.version} • ${update.date}`}>
                        <span className={`update-badge ${getBadgeClass(update.type)}`}>{update.type}</span>
                      </div>
                      <h2 className="update-title">{update.title}</h2>
                      <p className="update-description">{update.description}</p>
                      
                      <div className="update-details-section">
                        <div className="details-header">
                          <h3>Includes {update.points?.length ?? 0} updates <i className={`fa-solid fa-chevron-down ${isExpanded ? 'rotate' : ''}`}></i></h3>
                        </div>
                        {isExpanded && (
                          <ul className="details-list animate-down">
                            {update.points?.map((point, i) => (
                              <li key={i}>{point}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="empty-changelog glass-card">
              <i className="fa-solid fa-info-circle"></i>
              <p>No updates found for this page.</p>
              <button onClick={() => handlePageChange(1)} className="btn btn-secondary">Return to Page 1</button>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="changelog-pagination">
            <button 
              className="pagination-btn" 
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <i className="fa-solid fa-arrow-left"></i> Newer
            </button>
            <div className="pagination-info">
              Page {currentPage} of {totalPages}
            </div>
            <button 
              className="pagination-btn" 
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Older <i className="fa-solid fa-arrow-right"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Changelog;

